import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText } from "lucide-react";
import { apps, shippedApps } from "@/data/products";
import { PRODUCTS_ES } from "@/data/productsEs";
import { useLang } from "@/context/LangContext";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { AppleMark } from "@/components/sections/AvailableNow";

export default function ProductPage() {
  const { slug } = useParams();
  const { t, lang, localizePath } = useLang();
  const es = lang === "es";
  const idx = apps.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to={localizePath("/")} replace />;
  const app = apps[idx];
  const next = shippedApps[(shippedApps.findIndex((p) => p.slug === slug) + 1) % shippedApps.length];
  const esData = PRODUCTS_ES[slug] || {};
  const description = es && esData.description ? esData.description : app.description;
  const features = es && esData.features ? esData.features : app.features;
  const whatsNew = es && esData.whatsNew ? esData.whatsNew : app.whatsNew;
  const statusLabel = app.status === "coming" ? t("products.comingSoon") : app.status === "updated" ? t("products.updated") : t("products.available");

  return (
    <div data-testid="product-page" className="bg-paper pt-24 md:pt-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <FadeUp>
          <Link
            to={localizePath("/#products")}
            data-testid="back-to-products"
            className="group inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50 transition-colors hover:text-cobalt"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
            {t("productPage.back")}
          </Link>
        </FadeUp>

        <div className="mt-10 grid grid-cols-1 items-center gap-14 md:grid-cols-12">
          <div className="md:col-span-7">
            <FadeUp delay={0.05}>
              <img
                src={app.icon}
                alt={`${app.name} app icon`}
                data-testid="product-icon"
                className="h-20 w-20 rounded-[22%] border border-ink/10 shadow-[0_16px_32px_-16px_rgba(17,17,16,0.3)]"
              />
            </FadeUp>
            <FadeUp delay={0.12}>
              <h1 className="mt-8 text-4xl font-semibold leading-[1.05] tracking-tight text-ink md:text-6xl">
                {app.name}
              </h1>
            </FadeUp>
            <FadeUp delay={0.18}>
              <p className="mt-4 max-w-lg text-lg leading-snug text-ink/70">
                {es && app.taglineEs ? app.taglineEs : app.tagline}
              </p>
            </FadeUp>
            <FadeUp delay={0.24}>
              <p className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-medium uppercase tracking-[0.2em] text-ink/45">
                <span>{app.compatibility}</span>
                <span className="h-px w-5 bg-ink/20" aria-hidden="true" />
                <span className={app.status === "coming" ? "" : "text-cobalt"} data-testid="product-status">{statusLabel}</span>
                {app.pricing && (
                  <>
                    <span className="h-px w-5 bg-ink/20" aria-hidden="true" />
                    <span data-testid="product-pricing">{es ? app.pricing.es : app.pricing.en}</span>
                  </>
                )}
              </p>
            </FadeUp>
            <FadeUp delay={0.3} className="mt-10 flex flex-wrap items-center gap-4">
              {app.storeUrl ? (
                <a
                  href={app.storeUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="product-cta-store"
                  className="inline-flex items-center gap-3 rounded-full bg-ink px-7 py-3.5 text-white transition-colors duration-300 hover:bg-cobalt"
                >
                  <AppleMark className="h-5 w-5 fill-current" />
                  <span className="leading-none">
                    <span className="block text-[9px] uppercase tracking-wider opacity-80">{t("products.downloadOn")}</span>
                    <span className="block text-sm font-semibold">{t("products.store")}</span>
                  </span>
                </a>
              ) : (
                <span data-testid="product-coming-soon" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink/60">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cobalt" aria-hidden="true" />
                  {t("products.comingSoon")}
                </span>
              )}
              <Link
                to={localizePath(`/app/${app.slug}/privacy`)}
                data-testid="product-cta-privacy"
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-cobalt hover:text-cobalt"
              >
                {t("legalPage.privacy")}
              </Link>
              <Link
                to={localizePath(`/app/${app.slug}/terms`)}
                data-testid="product-cta-terms"
                className="inline-flex items-center gap-2 text-sm font-semibold text-ink/60 underline decoration-ink/20 underline-offset-4 transition-colors hover:text-cobalt hover:decoration-cobalt"
              >
                {t("legalPage.terms")}
              </Link>
            </FadeUp>
          </div>

          <div className="md:col-span-5">
            <FadeUp delay={0.15} amount={0.1} className="relative">
              <div className="overflow-hidden border border-ink/10 shadow-[0_30px_60px_-30px_rgba(17,17,16,0.3)]">
                <img
                  src={app.screenshots[0]}
                  alt={`${app.name} screenshot`}
                  data-testid="product-screenshot-main"
                  className={app.slug === "icd10-2026" ? "max-h-[620px] w-full object-contain bg-white" : "max-h-[480px] w-full object-cover object-top"}
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </div>

      <section data-testid="product-about" className="mt-24 border-t border-ink/10 bg-white py-20 md:mt-28 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-4">
            <FadeUp><Eyebrow>{t("productPage.about")}</Eyebrow></FadeUp>
          </div>
          <div className="md:col-span-8">
            {description.split("\n\n").map((para, i) => (
              <FadeUp key={i} delay={0.05 * i} amount={0.2}>
                <p data-testid={`about-para-${i}`} className="mb-6 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg">{para}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="device-gallery" className="border-t border-ink/10 bg-paper py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp><Eyebrow>{t("productPage.inHand")}</Eyebrow></FadeUp>
          <div className="mt-12 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-5">
            {app.screenshots.map((src, i) => (
              <FadeUp key={src} delay={0.08 * i} amount={0.2}>
                <div className={`overflow-hidden border border-ink/10 bg-white transition-transform duration-700 ease-out hover:-translate-y-1.5 ${i % 2 === 1 ? "sm:translate-y-6 hover:sm:translate-y-4" : ""}`}>
                  <img
                    src={src}
                    alt={`${app.name} screen ${i + 1}`}
                    data-testid={`gallery-shot-${i + 1}`}
                    loading="lazy"
                    className="w-full object-cover object-top"
                  />
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {whatsNew && (
        <section data-testid="changelog" className="border-t border-ink/10 bg-white py-20 md:py-28">
          <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 md:grid-cols-12 md:px-10">
            <div className="md:col-span-4">
              <FadeUp><Eyebrow>{t("productPage.whatsNew")}</Eyebrow></FadeUp>
            </div>
            <div className="md:col-span-8">
              {whatsNew.split("\n\n").map((para, i) => (
                <FadeUp key={i} delay={0.08 * i} amount={0.3}>
                  <p data-testid={`changelog-item-${i}`} className="mb-5 max-w-2xl text-base leading-relaxed text-ink/70">{para}</p>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      <section data-testid="features" className="border-t border-ink/10 bg-paper py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-4">
            <FadeUp><Eyebrow>{t("productPage.keyFeatures")}</Eyebrow></FadeUp>
          </div>
          <div className="md:col-span-8">
            <ul>
              {features.map((f, i) => (
                <FadeUp key={i} delay={0.06 * i} amount={0.4}>
                  <li data-testid={`feature-item-${i}`} className="flex items-baseline gap-4 border-t border-ink/10 py-4 text-base leading-relaxed text-ink/75 last:border-b">
                    <span className="font-sans text-sm font-light text-cobalt">{String(i + 1).padStart(2, "0")}</span>
                    {f}
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="legal-links" className="border-t border-ink/10 bg-ink py-20 text-paper md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp><Eyebrow dark>{t("productPage.documents")}</Eyebrow></FadeUp>
          <div className="mt-10 grid grid-cols-1 gap-px bg-paper/10 sm:grid-cols-2">
            {[
              { to: `/app/${app.slug}/privacy`, label: t("legalPage.privacy"), id: "privacy" },
              { to: `/app/${app.slug}/terms`, label: t("legalPage.terms"), id: "terms" },
            ].map((l) => (
              <FadeUp key={l.id} amount={0.3}>
                <Link
                  to={localizePath(l.to)}
                  data-testid={`legal-link-${l.id}`}
                  className="group flex h-full items-center justify-between gap-5 bg-ink p-7 transition-colors duration-300 hover:bg-[#181817] md:p-9"
                >
                  <span className="flex items-center gap-4 text-lg tracking-tight">
                    <FileText className="h-5 w-5 shrink-0 text-cobalt" strokeWidth={1.5} />
                    {l.label}
                  </span>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-paper/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-cobalt" />
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white">
        <Link
          to={localizePath(`/app/${next.slug}`)}
          data-testid="next-product-link"
          className="group mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 py-14 md:px-10 md:py-20"
        >
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-ink/40">{t("productPage.nextProduct")}</p>
            <p className="mt-4 flex items-center gap-4 text-3xl font-semibold tracking-tight text-ink transition-colors duration-300 group-hover:text-cobalt md:text-4xl">
              <img src={next.icon} alt="" className="h-12 w-12 rounded-[22%] border border-ink/10 md:h-14 md:w-14" />
              {next.name}
            </p>
          </div>
          <ArrowUpRight className="h-8 w-8 shrink-0 text-ink/30 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-cobalt md:h-12 md:w-12" />
        </Link>
      </section>
    </div>
  );
}
